import {EmptyString, isNonEmptyString, NonEmptyString, StringUnion} from 'Core/models/EmptyString';
import {ToMatchOptions} from 'Shared/utilities/MapDiscriminatedUnion';

import {isValidDate} from 'ApplicationEntry/models/Date';
import {InvalidZipCode, isValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import {Async, Empty} from 'Core/models/Async';
import {CaseNumber, CaseNumberError, UserAssignedCaseNumber} from './CaseNumber';
import {FormNumber, FormNumberError, UserAssignedFormNumber} from './FormNumber';

export class InvalidNewYorkBillingAgent {
  public readonly key = 'InvalidNewYorkBillingAgent';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly controlNumber: StringUnion,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly account: StringUnion,
    public readonly draftDay: StringUnion,
    public readonly billingMode: StringUnion,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion, 
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly signatureState: StringUnion,
    public readonly dateSigned: Date | '',
    public readonly ink: boolean,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly pac: boolean,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

type BillingAgentProp = {
  caseNumber: CaseNumber;
  controlNumber: StringUnion;
  caseName: StringUnion;
  transit: StringUnion;
  account: StringUnion;
  draftDay: StringUnion;
  billingMode: StringUnion;
  achTransitNumber: StringUnion,
  achAccountNumber: StringUnion,
  achAccountType: StringUnion,
  achPayerName: StringUnion,
  achDraftDay: StringUnion,
  achAuthorizationDate: Date | '',
  issueDate: Date | '';
  formNumber: FormNumber;
  payor: StringUnion;
  state: StringUnion;
  zip: ZipCode;
  signatureState: StringUnion;
  dateSigned: Date | '';
  ink: boolean;
  payroll: boolean;
  directBill: boolean;
  pac: boolean;
  transitNumber: StringUnion;
  bankAccountNumber: StringUnion;
  dueDate: StringUnion;
  accountHolderName: StringUnion;
  bankType: StringUnion;
  authorizedDate: Date | '';
};

export class ValidNewYorkBillingAgent {
  public static createFromApp({
    caseNumber,
    controlNumber,
    caseName,
    transit,
    account,
    draftDay,
    billingMode,
    achTransitNumber,
    achAccountNumber,
    achAccountType,
    achPayerName,
    achDraftDay,
    achAuthorizationDate,
    issueDate,
    formNumber,
    payor,
    state,
    zip,
    signatureState,
    dateSigned,
    ink,
    payroll,
    directBill,
    pac,
    transitNumber,
    bankAccountNumber,
    dueDate,
    accountHolderName,
    bankType,
    authorizedDate,
  }: BillingAgentProp) {
    return this.create(
      caseNumber,
      controlNumber,
      caseName,
      transit,
      account,
      draftDay,
      billingMode,
      achTransitNumber,
      achAccountNumber,
      achAccountType,
      achPayerName,
      achDraftDay,
      achAuthorizationDate,
      issueDate,
      formNumber,
      payor,
      state,
      zip,
      signatureState,
      dateSigned,
      ink,
      payroll,
      directBill,
      pac,
      transitNumber,
      bankAccountNumber,
      dueDate,
      accountHolderName,
      bankType,
      authorizedDate
    );
  }

  public static create(
    caseNumber: CaseNumber,
    controlNumber: StringUnion,
    caseName: StringUnion,
    transit: StringUnion,
    account: StringUnion,
    draftDay: StringUnion,
    billingMode: StringUnion,
    achTransitNumber: StringUnion,
    achAccountNumber: StringUnion,
    achAccountType: StringUnion,
    achPayerName: StringUnion,
    achDraftDay: StringUnion,
    achAuthorizationDate: Date | '',
    issueDate: Date | '',
    formNumber: FormNumber,
    payor: StringUnion,
    state: StringUnion,
    zip: ZipCode,
    signatureState: StringUnion,
    dateSigned: Date | '',
    ink: boolean,
    payroll: boolean,
    directBill: boolean,
    pac: boolean,
    transitNumber: StringUnion,
    bankAccountNumber: StringUnion,
    dueDate: StringUnion,
    accountHolderName: StringUnion,
    bankType: StringUnion,
    authorizedDate: Date | '',
  ) {
    return isNonEmptyString(billingMode) && isValidZipCode(zip) && isValidDate(issueDate) && isValidDate(dateSigned)
      ? new ValidNewYorkBillingAgent(
          caseNumber,
          controlNumber,
          caseName,
          transit,
          account,
          draftDay,
          billingMode,
          achTransitNumber,
          achAccountNumber,
          achAccountType,
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          issueDate,
          formNumber,
          payor,
          state,
          zip,
          signatureState,
          dateSigned,
          ink,
          payroll,
          directBill,
          pac,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate,
        )
      : new InvalidNewYorkBillingAgent(
          caseNumber,
          controlNumber,
          caseName,
          transit,
          account,
          draftDay,
          billingMode,
          achTransitNumber,
          achAccountNumber,
          achAccountType,          
          achPayerName,
          achDraftDay,
          achAuthorizationDate,
          issueDate,
          formNumber,
          payor,
          state,
          zip,
          signatureState,
          dateSigned,
          ink,
          payroll,
          directBill,
          pac,
          transitNumber,
          bankAccountNumber,
          dueDate,
          accountHolderName,
          bankType,
          authorizedDate
        );
  }
  public readonly key = 'ValidNewYorkBillingAgent';

  constructor(
    public readonly caseNumber: CaseNumber,
    public readonly controlNumber: StringUnion,
    public readonly caseName: StringUnion,
    public readonly transit: StringUnion,
    public readonly account: StringUnion,
    public readonly draftDay: StringUnion,
    public readonly billingMode: StringUnion,
    public readonly achTransitNumber: StringUnion,
    public readonly achAccountNumber: StringUnion,
    public readonly achAccountType: StringUnion,
    public readonly achPayerName: StringUnion,
    public readonly achDraftDay: StringUnion,
    public readonly achAuthorizationDate: Date | '',
    public readonly issueDate: Date | '',
    public readonly formNumber: FormNumber,
    public readonly payor: StringUnion,
    public readonly state: StringUnion,
    public readonly zip: ZipCode,
    public readonly signatureState: StringUnion,
    public readonly dateSigned: Date | '',
    public readonly ink: boolean,
    public readonly payroll: boolean,
    public readonly directBill: boolean,
    public readonly pac: boolean,
    public readonly transitNumber: StringUnion,
    public readonly bankAccountNumber: StringUnion,
    public readonly dueDate: StringUnion,
    public readonly accountHolderName: StringUnion,
    public readonly bankType: StringUnion,
    public readonly authorizedDate: Date | '',
  ) {}
}

export type NewYorkBillingAgent = ValidNewYorkBillingAgent | InvalidNewYorkBillingAgent;

export type NewYorkBillingAgentOptions<TReturn> = ToMatchOptions<NewYorkBillingAgent, 'key', TReturn>;

export const matchNewYorkBillingAgentOptions = <TReturn>(
  billingAgent: NewYorkBillingAgent,
  options: NewYorkBillingAgentOptions<TReturn>
): TReturn => {
  switch (billingAgent.key) {
    case 'InvalidNewYorkBillingAgent':
      return options.InvalidNewYorkBillingAgent(billingAgent);
    case 'ValidNewYorkBillingAgent':
      return options.ValidNewYorkBillingAgent(billingAgent);
  }
};

export const EmptyNewYorkBillingAgent: NewYorkBillingAgent = new ValidNewYorkBillingAgent(
  new UserAssignedCaseNumber(''),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  '',
  '',
  new UserAssignedFormNumber('', '', '', '', ''),
  new EmptyString(),
  new EmptyString(),
  new InvalidZipCode(''),
  new EmptyString(),
  '',
  false,
  false,
  false,
  false,
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  new EmptyString(),
  ''
);

export const changeCaseNumber = (caseNumber: Async<string>) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, caseNumber});

export const changeUserCaseNumber = (caseNumber: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, caseNumber: UserAssignedCaseNumber.create(caseNumber)});

export const changeCaseNumberError = (caseNumber: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, caseNumber: CaseNumberError.create(caseNumber)});

export const changeControlNumber = (controlNumber: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({
    ...app,
    controlNumber: NonEmptyString.create(controlNumber),
  });

export const changeCaseName = (caseName: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({
    ...app,
    caseName: NonEmptyString.create(caseName),
  });

export const changeTransit = (transit: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, transit: NonEmptyString.create(transit)});

export const changeAccount = (account: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, account: NonEmptyString.create(account)});

export const changeDraftDay = (draftDay: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, draftDay: NonEmptyString.create(draftDay)});

export const changeBillingMode = (billingMode: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, billingMode: NonEmptyString.create(billingMode)});

export const changeState = (state: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, state: NonEmptyString.create(state)});

export const changeZip = (zip: ZipCode) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, zip});

export const changeIssueDate = (issueDate: Date | '') => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, issueDate});

export const changeFormNumber = (formNumber: Async<UserAssignedFormNumber>) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({
    ...app,
    formNumber,
  });

export const changeUserFormNumber = (
  formNumber: string,
  documentShortName: string,
  fipscoDocumentName: string,
  documentLongName: string,
  documentName: string
) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({
    ...app,
    formNumber: UserAssignedFormNumber.create(
      formNumber,
      documentShortName,
      fipscoDocumentName,
      documentShortName,
      fipscoDocumentName
    ),
  });

export const changeFormNumberError = (formNumber: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, formNumber: FormNumberError.create(formNumber)});

export const changeDate = (dateSigned: Date | '') => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, dateSigned});

export const changeAuthorizedDate = (achAuthorizationDate: Date | '') => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, achAuthorizationDate});

export const changePayor = (payor: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, payor: NonEmptyString.create(payor)});

export const changeSignatureState = (signatureState: string) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, signatureState: NonEmptyString.create(signatureState)});

export const changeInk = (ink: boolean) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, ink});

export const changePayroll = (payroll: boolean) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, payroll});

export const changeDirectBill = (directBill: boolean) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, directBill});

export const changePac = (pac: boolean) => (app: NewYorkBillingAgent) =>
  ValidNewYorkBillingAgent.createFromApp({...app, pac});
